import React, { useMemo } from "react";
import { FilterX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UnnotifiedLeadBadge } from "@/components/UnnotifiedLeadBadge";

const LABELS = {
  lead_ingest: "Lead Ingest", site_measure: "Site Measure", design: "Design Phase",
  contract_sent: "Contract Sent", fabrication: "Shop Fabrication",
  installation: "Crew Installation", completed: "Job Completed",
};

const money = value => new Intl.NumberFormat("en-CA", {
  style: "currency", currency: "CAD", maximumFractionDigits: 0,
}).format(value || 0);

const signal = item => {
  if (item.stage === "design" && item.retail_potential) return money(item.retail_potential);
  if (item.stage === "contract_sent" && item.days_in_stage != null) return `${item.days_in_stage.toFixed(1)} days`;
  if (item.schedule_date) return item.schedule_date;
  return "—";
};

export const LifecycleProjectWorkspace = ({ items, activeStage, onClear, flushTop = false }) => {
  const visibleItems = useMemo(
    () => activeStage ? items.filter(item => item.stage === activeStage) : items,
    [activeStage, items],
  );
  return (
    <section className={`${flushTop ? "" : "mt-3"} overflow-hidden rounded-md border border-slate-200 bg-white`} data-testid="lifecycle-project-workspace">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3" data-testid="pipeline-filter-toolbar">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900" data-testid="pipeline-workspace-title">Lifecycle Project Workspace</h2>
          {activeStage && <Badge variant="secondary" data-testid="active-filter-badge">{LABELS[activeStage]}</Badge>}
          <span className="text-xs text-slate-500" data-testid="pipeline-filter-result-count">{visibleItems.length} client file{visibleItems.length === 1 ? "" : "s"}</span>
        </div>
        {activeStage && (
          <Button type="button" variant="ghost" size="sm" onClick={onClear} data-testid="clear-filter-button">
            <FilterX className="mr-1.5 h-4 w-4" />Clear filter
          </Button>
        )}
      </header>
      {visibleItems.length > 0 ? (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse" data-testid="lifecycle-data-table">
              <thead><tr className="bg-slate-50 text-left text-xs text-slate-500">
                <th className="px-4 py-2.5 font-medium">Client file</th><th className="px-4 py-2.5 font-medium">Phase</th>
                <th className="px-4 py-2.5 font-medium">Status</th><th className="px-4 py-2.5 font-medium">Address</th>
                <th className="px-4 py-2.5 text-right font-medium">Stage signal</th>
              </tr></thead>
              <tbody>{visibleItems.map(item => (
                <tr key={`${item.entity_type}-${item.item_id}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pipeline-item-${item.item_id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900" data-testid={`pipeline-item-name-${item.item_id}`}>
                    <div className="flex flex-col items-start gap-1">
                      <span>{item.customer_name || item.name}</span>
                      <UnnotifiedLeadBadge status={item.admin_notification_status} recordId={item.item_id} context="lifecycle-table" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600" data-testid={`pipeline-item-stage-${item.item_id}`}>{LABELS[item.stage]}</td>
                  <td className="px-4 py-3 text-sm capitalize text-slate-600" data-testid={`pipeline-item-status-${item.item_id}`}>{item.status?.replaceAll("_", " ") || "Open"}</td>
                  <td className="max-w-[280px] truncate px-4 py-3 text-sm text-slate-600" data-testid={`pipeline-item-address-${item.item_id}`}>{item.address || <span className="text-rose-600">Address required</span>}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-slate-700" data-testid={`pipeline-item-signal-${item.item_id}`}>{signal(item)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="divide-y divide-slate-100 md:hidden" data-testid="lifecycle-mobile-list">
            {visibleItems.map(item => (
              <article key={`${item.entity_type}-${item.item_id}`} className="space-y-2 p-4" data-testid={`pipeline-mobile-item-${item.item_id}`}>
                <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="font-medium text-slate-900" data-testid={`pipeline-mobile-name-${item.item_id}`}>{item.customer_name || item.name}</p><div className="mt-1"><UnnotifiedLeadBadge status={item.admin_notification_status} recordId={item.item_id} context="lifecycle-mobile" /></div></div><Badge variant="outline" data-testid={`pipeline-mobile-stage-${item.item_id}`}>{LABELS[item.stage]}</Badge></div>
                <div className="flex items-end justify-between gap-3 text-sm text-slate-500"><span data-testid={`pipeline-mobile-address-${item.item_id}`}>{item.address || "Address required"}</span><span className="shrink-0 font-medium text-slate-700" data-testid={`pipeline-mobile-signal-${item.item_id}`}>{signal(item)}</span></div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="pipeline-filter-empty-state">No client files are currently in this phase.</div>
      )}
    </section>
  );
};